import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowLeft, Zap, Check } from "lucide-react";
import useSEO from "@/lib/useSEO";

export default function InstantTransferFeature() {
  useSEO({
    title: "Instant Transfer",
    description: "Move money between y ℶα members in seconds. No rails, no waits. Notes and receipts included.",
    path: "/patrons/instant-transfer",
  });
  return (
    <main data-testid="patrons-instant" className="bg-bg text-white min-h-screen">
      <section className="max-w-6xl mx-auto px-6 lg:px-10 pt-24 pb-24">
        <Link to="/" data-testid="patrons-back" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-muted-fg hover:text-gold transition-colors">
          <ArrowLeft size={14} /> Back to the Landing
        </Link>

        <div className="mt-10 grid grid-cols-12 gap-10">
          <div className="col-span-12 lg:col-span-7">
            <div className="text-xs uppercase tracking-[0.3em] text-gold mb-6">— 03 / For Patrons · Instant Transfer</div>
            <h1 className="font-display text-5xl lg:text-7xl tracking-tighter font-light leading-[0.95]">
              Seconds,<br/><span className="italic gold-text">not days.</span>
            </h1>
            <p className="mt-8 text-secondary-fg text-lg leading-relaxed max-w-xl font-light">
              Send to any y ℶα member by email and the funds appear in their wallet within seconds — even across currencies. No SWIFT, no IBAN, no waiting hours for batch processing.
            </p>
            <div className="mt-12 flex flex-wrap gap-4">
              <Link to="/register" className="btn-gold inline-flex items-center gap-2">Send your first transfer <ArrowUpRight size={16} /></Link>
              <Link to="/login" className="btn-ghost">Sign in</Link>
            </div>
          </div>

          <div className="col-span-12 lg:col-span-5">
            <div className="glass p-8 rounded-md">
              <div className="text-xs uppercase tracking-[0.3em] text-muted-fg mb-3">Latest transfer</div>
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 rounded-full bg-elevated border hairline-strong flex items-center justify-center font-display text-gold">A</div>
                <Zap className="text-gold" size={18}/>
                <div className="w-12 h-12 rounded-full bg-elevated border hairline-strong flex items-center justify-center font-display text-gold">B</div>
              </div>
              <div className="flex justify-between text-sm py-2"><span className="text-muted-fg">Amount</span><span className="font-mono">€ 1,250.00</span></div>
              <div className="flex justify-between text-sm py-2"><span className="text-muted-fg">Settled in</span><span className="text-gold">2.4 seconds</span></div>
              <div className="flex justify-between text-sm py-2"><span className="text-muted-fg">Fee</span><span>—</span></div>
              <div className="flex justify-between text-sm py-2"><span className="text-muted-fg">Receipt</span><span className="text-gold">Logged</span></div>
            </div>
          </div>
        </div>

        <div className="mt-24 grid grid-cols-1 md:grid-cols-3 gap-px bg-white/5 border hairline-strong">
          {[
            ["Network speed", "y ℶα to y ℶα transfers settle within the network in 1–3 seconds."],
            ["No banking rails", "Skip SWIFT, SEPA delays, and weekends. Our ledger updates immediately."],
            ["No member fees", "Zero surcharge between y ℶα members, regardless of amount or currency."],
            ["Notes attached", "Add a discreet private note that only your counterparty sees."],
            ["Signed receipts", "Both parties get a timestamped, signed receipt in their ledger."],
            ["Cross-currency", "Send EUR, recipient gets USD — instantly. FX baked in."],
          ].map(([t, b]) => (
            <div key={t} className="bg-bg p-8">
              <div className="flex items-start gap-3">
                <Check className="text-gold mt-1 shrink-0" size={18} strokeWidth={1.5} />
                <div>
                  <h3 className="font-display text-xl mb-2">{t}</h3>
                  <p className="text-secondary-fg text-sm leading-relaxed font-light">{b}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-24 text-center">
          <h2 className="font-display text-4xl lg:text-5xl tracking-tight font-light">
            Instant. <span className="italic gold-text">As intended.</span>
          </h2>
          <div className="mt-8 flex justify-center gap-4">
            <Link to="/register" className="btn-gold inline-flex items-center gap-2">Join y ℶα <ArrowUpRight size={16} /></Link>
          </div>
        </div>
      </section>
    </main>
  );
}
